import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { PortableText } from "@portabletext/react";
import { sanityClient, urlForImage } from "../utils/sanityClient";

const POST_QUERY = `*[_type == "post" && slug.current == $slug && publishedAt <= now()][0]{
  title,
  excerpt,
  mainImage,
  body,
  publishedAt,
  "authorName": author->name
}`;

const MORE_POSTS_QUERY = `*[_type == "post" && slug.current != $slug && publishedAt <= now()] | order(publishedAt desc)[0...5]{
  _id,
  title,
  slug,
  mainImage,
  publishedAt
}`;

const BlogPostPage = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [morePosts, setMorePosts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [fetchError, setFetchError] = useState(false);

  useEffect(() => {
    setIsLoading(true);
    sanityClient
      .fetch(POST_QUERY, { slug })
      .then((data) => setPost(data))
      .catch(() => setFetchError(true))
      .finally(() => setIsLoading(false));

    sanityClient
      .fetch(MORE_POSTS_QUERY, { slug })
      .then((data) => setMorePosts(data || []))
      .catch(() => setMorePosts([]));
  }, [slug]);

  return (
    <div className="page-container overflow-y-auto standard-padding font-poppins">
      <Helmet>
        <title>{post ? `${post.title} | LasGidi Restaurant` : "Blog | LasGidi Restaurant"}</title>
        {post && (
          <>
            <meta name="description" content={post.excerpt || ""} />
            <meta property="og:type" content="article" />
            <meta property="og:site_name" content="LasGidi Restaurant" />
            <meta property="og:title" content={`${post.title} | LasGidi Restaurant`} />
            <meta property="og:description" content={post.excerpt || ""} />
            <meta property="og:url" content={`https://lasgidirestaurant.com/blog/${slug}`} />
            {post.mainImage && (
              <meta
                property="og:image"
                content={urlForImage(post.mainImage).width(1200).height(630).fit("crop").url()}
              />
            )}
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content={`${post.title} | LasGidi Restaurant`} />
            <meta name="twitter:description" content={post.excerpt || ""} />
            {post.mainImage && (
              <meta
                name="twitter:image"
                content={urlForImage(post.mainImage).width(1200).height(630).fit("crop").url()}
              />
            )}
          </>
        )}
      </Helmet>

      <div className="max-w-[1100px] mx-auto py-10">
        <Link to="/blog" className="text-brand-orange text-sm no-underline hover:underline">
          &larr; Back to Blog
        </Link>

        {isLoading && <p className="text-white/60 py-20 text-center">Loading...</p>}

        {!isLoading && (fetchError || !post) && (
          <p className="text-white/60 py-20 text-center">
            This post couldn't be found.
          </p>
        )}

        {!isLoading && post && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-16 lg:gap-20 mt-6 items-start">
            <article className="lg:col-span-2">
              <h1 className="font-prata font-bold text-3xl text-white mb-3">{post.title}</h1>
              <p className="text-xs text-brand-orange uppercase tracking-wide mb-6">
                {post.authorName || "LasGidi Restaurant"}
                {post.publishedAt &&
                  ` · ${new Date(post.publishedAt).toLocaleDateString("en-GB", {
                    day: "numeric",
                    month: "short",
                    year: "numeric",
                  })}`}
              </p>

              {post.mainImage && (
                <img
                  src={urlForImage(post.mainImage).width(1200).height(675).fit("crop").url()}
                  alt={post.title}
                  className="w-full aspect-[16/9] object-cover rounded-[10px] mb-8"
                />
              )}

              <div className="text-white/90 leading-relaxed [&_p]:mb-4 [&_h2]:font-prata [&_h2]:font-bold [&_h2]:text-brand-orange [&_h2]:text-xl [&_h2]:mt-8 [&_h2]:mb-3 [&_h3]:font-prata [&_h3]:font-bold [&_h3]:text-brand-orange [&_h3]:text-lg [&_h3]:mt-6 [&_h3]:mb-2 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:mb-4 [&_ul]:space-y-2 [&_li]:leading-relaxed [&_blockquote]:my-6 [&_blockquote]:border-l-4 [&_blockquote]:border-brand-orange [&_blockquote]:pl-4 [&_blockquote]:italic [&_blockquote]:text-white/80 [&_a]:text-brand-orange [&_a]:underline">
                {post.body && <PortableText value={post.body} />}
              </div>
            </article>

            <aside className="lg:col-span-1">
              <h2 className="font-prata font-bold text-lg text-white mb-4 border-b-2 border-brand-orange pb-3">
                More Posts
              </h2>

              {morePosts.length === 0 ? (
                <p className="text-sm text-white/50">No other posts yet.</p>
              ) : (
                <div className="flex flex-col gap-8">
                  {morePosts.map((mp) => (
                    <Link
                      key={mp._id}
                      to={`/blog/${mp.slug?.current}`}
                      className="flex gap-3 no-underline text-white group"
                    >
                      {mp.mainImage && (
                        <img
                          src={urlForImage(mp.mainImage).width(160).height(90).fit("crop").url()}
                          alt={mp.title}
                          className="w-[100px] aspect-[16/9] object-cover rounded-md flex-shrink-0"
                        />
                      )}
                      <div className="min-w-0">
                        <h3 className="font-poppins text-sm font-semibold leading-snug group-hover:text-brand-orange transition-colors">
                          {mp.title}
                        </h3>
                        {mp.publishedAt && (
                          <p className="text-xs text-white/50 mt-1.5">
                            {new Date(mp.publishedAt).toLocaleDateString("en-GB", {
                              day: "numeric",
                              month: "short",
                              year: "numeric",
                            })}
                          </p>
                        )}
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </aside>
          </div>
        )}
      </div>
    </div>
  );
};

export default BlogPostPage;
